import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import confetti from "canvas-confetti";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { MODULE_THEMES } from "../data/modules";
import { playCorrect, playWrong, playBadge } from "../lib/sound";
import {
  ArrowLeft,
  Check,
  X,
  Trophy,
  Flame,
  Sparkles,
  Award,
  ChevronRight,
  PartyPopper,
  RotateCcw,
  LifeBuoy,
  Phone,
  MessageSquare,
} from "lucide-react";

// Modules that must always surface crisis resources (safety-first).
const CRISIS_MODULES = new Set(["crisis_prevention"]);

function CrisisBanner() {
  return (
    <div
      className="rounded-3xl border-2 p-5 mb-5"
      style={{ borderColor: "#5B7DB1", backgroundColor: "#EAEFF7" }}
      data-testid="crisis-banner"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-2xl flex items-center justify-center shrink-0" style={{ backgroundColor: "#5B7DB1" }}>
          <LifeBuoy className="w-5 h-5 text-white" />
        </div>
        <div>
          <p className="font-head font-extrabold text-[15px]" style={{ color: "#2D3748" }}>
            In crisis or thinking of harming yourself or someone else? Help is available right now.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <a
              href="tel:988"
              className="inline-flex items-center gap-1.5 bg-white border border-black/10 rounded-full px-4 py-2 text-sm font-semibold hover:-translate-y-0.5 transition-transform"
              data-testid="crisis-call-988"
            >
              <Phone className="w-4 h-4" style={{ color: "#5B7DB1" }} /> Call or text 988
            </a>
            <a
              href="sms:741741?&body=HOME"
              className="inline-flex items-center gap-1.5 bg-white border border-black/10 rounded-full px-4 py-2 text-sm font-semibold hover:-translate-y-0.5 transition-transform"
              data-testid="crisis-text-line"
            >
              <MessageSquare className="w-4 h-4" style={{ color: "#5B7DB1" }} /> Text HOME to 741741
            </a>
            <a
              href="tel:911"
              className="inline-flex items-center gap-1.5 bg-white border border-black/10 rounded-full px-4 py-2 text-sm font-semibold hover:-translate-y-0.5 transition-transform"
              data-testid="crisis-call-911"
            >
              <Phone className="w-4 h-4" style={{ color: "#C0392B" }} /> Call 911 (emergency)
            </a>
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            988 Suicide &amp; Crisis Lifeline is free, confidential and available 24/7. This program is educational and not a substitute for professional care.
          </p>
        </div>
      </div>
    </div>
  );
}

const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const prepare = (list) =>
  shuffle(list).map((q) => ({ ...q, options: shuffle(q.options) }));

const CORRECT_COLOR = "#4CAF7A";
const WRONG_COLOR = "#E27D60";
const BADGE_SOUND_DELAY_MS = 500;
const FIREWORKS_DELAY_MS = 150;
const FIREWORKS_DURATION_MS = 1400;

const optionCircleBg = ({ result, isCorrect, showWrong, isSel, primary }) => {
  if (result) {
    if (isCorrect) return CORRECT_COLOR;
    if (showWrong) return WRONG_COLOR;
    return "transparent";
  }
  if (isSel) return primary;
  return "transparent";
};

function fireworks() {
  if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  const colors = ["#E27D60", "#85B09A", "#7B9EBF", "#E8A87C", "#9D8BB6", "#5BA8A0", "#D4AF37"];
  const end = Date.now() + FIREWORKS_DURATION_MS;
  (function frame() {
    confetti({ particleCount: 5, angle: 60, spread: 60, origin: { x: 0 }, colors, scalar: 0.9 });
    confetti({ particleCount: 5, angle: 120, spread: 60, origin: { x: 1 }, colors, scalar: 0.9 });
    if (Date.now() < end) requestAnimationFrame(frame);
  })();
  confetti({ particleCount: 90, spread: 100, origin: { y: 0.6 }, colors, scalar: 1 });
}

export default function ModulePlay() {
  const { moduleKey } = useParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const t = MODULE_THEMES[moduleKey] || MODULE_THEMES.anxiety;

  const [questions, setQuestions] = useState([]);
  const [idx, setIdx] = useState(0);
  const [selected, setSelected] = useState([]);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [sessionPoints, setSessionPoints] = useState(0);
  const [round, setRound] = useState(1);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const activeQ = useRef(null);

  useEffect(() => {
    Promise.all([
      api.get(`/modules/${moduleKey}/questions`),
      api.get("/modules"),
    ])
      .then(([q, mods]) => {
        setQuestions(prepare(q.data));
        const meta = mods.data.find((m) => m.key === moduleKey);
        setName(meta?.name || moduleKey);
      })
      .finally(() => setLoading(false));
  }, [moduleKey]);

  const restart = useCallback(() => {
    api.get(`/modules/${moduleKey}/questions`).then((r) => setQuestions(prepare(r.data)));
    setIdx(0);
    setSelected([]);
    setResult(null);
    setSessionPoints(0);
    setRound((r) => r + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [moduleKey]);

  const question = questions[idx];

  const toggle = (id) => {
    if (result) return;
    if (question.multi) {
      setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
    } else {
      setSelected([id]);
    }
  };

  const submit = async () => {
    if (!selected.length || submitting) return;
    setSubmitting(true);
    const qid = question.id;
    const chosen = selected;
    try {
      const { data } = await api.post(`/questions/${qid}/answer`, {
        selected_ids: chosen,
      });
      setResult(data);
      activeQ.current = qid;
      if (data.is_correct) {
        playCorrect();
        if (data.new_badges?.length) setTimeout(playBadge, BADGE_SOUND_DELAY_MS);
      } else {
        playWrong();
      }
      if (data.points_awarded) setSessionPoints((p) => p + data.points_awarded);
      refreshUser();

      // Fetch the AI message separately so the result/points/fireworks are instant.
      setFeedbackLoading(true);
      api
        .post(`/questions/${qid}/feedback`, { selected_ids: chosen })
        .then((r) => {
          if (activeQ.current === qid)
            setResult((prev) => (prev ? { ...prev, ai_feedback: r.data.ai_feedback } : prev));
        })
        .catch(() => {
          if (activeQ.current === qid)
            setResult((prev) =>
              prev
                ? {
                    ...prev,
                    ai_feedback:
                      prev.ai_feedback ||
                      (prev.is_correct
                        ? "Wonderful work — you're building real skills. Keep going."
                        : "That's okay — every attempt is progress. Notice what you learned."),
                  }
                : prev
            );
        })
        .finally(() => {
          if (activeQ.current === qid) setFeedbackLoading(false);
        });
    } catch (e) {
      if (e.response?.status === 402) {
        toast.info("Your free preview has ended — subscribe to keep going!");
        navigate("/subscribe");
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const next = () => {
    activeQ.current = null;
    setFeedbackLoading(false);
    setResult(null);
    setSelected([]);
    if (idx + 1 < questions.length) setIdx(idx + 1);
    else {
      setIdx(questions.length); // finished
      setTimeout(fireworks, FIREWORKS_DELAY_MS);
    }
  };

  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">
        Loading…
      </div>
    );

  const finished = idx >= questions.length && questions.length > 0;

  return (
    <div className="min-h-screen" style={{ backgroundColor: t.bg }}>
      <div className="max-w-2xl mx-auto px-5 py-8">
        {CRISIS_MODULES.has(moduleKey) && <CrisisBanner />}
        <button
          onClick={() => navigate("/hub")}
          className="flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors"
          data-testid="back-to-hub"
        >
          <ArrowLeft className="w-4 h-4" /> All programs
        </button>

        <div className="mt-4 flex items-center justify-between">
          <h1 className="font-head font-extrabold text-xl" style={{ color: t.primary }}>
            {name}
          </h1>
          <span className="flex items-center gap-1 text-sm font-bold" style={{ color: "#D4AF37" }}>
            <Trophy className="w-4 h-4" /> +{sessionPoints}
          </span>
        </div>

        {!finished && questions.length > 0 && (
          <>
            <div className="mt-3 h-2 rounded-full bg-black/5 overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${((idx + (result ? 1 : 0)) / questions.length) * 100}%`,
                  backgroundColor: t.primary,
                }}
              />
            </div>
            <p className="text-xs font-semibold text-muted-foreground mt-2">
              Question {idx + 1} of {questions.length}
            </p>

            <div className="bg-white rounded-3xl p-7 mt-5 shadow-sm border border-black/5 animate-fade-up" key={question.id}>
              {question.multi && (
                <span
                  className="inline-block text-xs font-bold px-3 py-1 rounded-full mb-4"
                  style={{ backgroundColor: t.bg, color: t.primary }}
                >
                  Select all that apply
                </span>
              )}
              <h2 className="font-head font-bold text-xl leading-snug">{question.text}</h2>

              <div className="mt-6 space-y-3">
                {question.options.map((o) => {
                  const isSel = selected.includes(o.id);
                  const isCorrect = result?.correct_ids.includes(o.id);
                  const showWrong = result && isSel && !isCorrect;
                  let style = {};
                  if (result) {
                    if (isCorrect) style = { borderColor: CORRECT_COLOR, backgroundColor: "#F0FBF4" };
                    else if (showWrong) style = { borderColor: WRONG_COLOR, backgroundColor: "#FEF2F2" };
                  } else if (isSel) {
                    style = { borderColor: t.primary, backgroundColor: t.bg };
                  }
                  return (
                    <button
                      key={o.id}
                      onClick={() => toggle(o.id)}
                      disabled={!!result}
                      className="w-full text-left flex items-center gap-3 border-2 rounded-2xl px-5 py-4 transition-colors disabled:cursor-default"
                      style={{ borderColor: "rgba(0,0,0,0.08)", ...style }}
                      data-testid={`option-${o.id}`}
                    >
                      <span
                        className="w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0"
                        style={{
                          borderColor: isSel || (result && isCorrect) ? style.borderColor || t.primary : "rgba(0,0,0,0.15)",
                          backgroundColor: optionCircleBg({ result, isCorrect, showWrong, isSel, primary: t.primary }),
                        }}
                      >
                        {result && isCorrect && <Check className="w-4 h-4 text-white" />}
                        {showWrong && <X className="w-4 h-4 text-white" />}
                        {!result && isSel && <Check className="w-4 h-4 text-white" />}
                      </span>
                      <span className="text-[15px]">{o.text}</span>
                    </button>
                  );
                })}
              </div>

              {!result && (
                <button
                  onClick={submit}
                  disabled={!selected.length || submitting}
                  className="mt-6 w-full text-white font-semibold py-4 rounded-full hover:-translate-y-0.5 transition-transform disabled:opacity-40 disabled:translate-y-0"
                  style={{ backgroundColor: t.primary }}
                  data-testid="submit-answer"
                >
                  {submitting ? "Checking…" : "Submit answer"}
                </button>
              )}
            </div>
          </>
        )}

        {finished && (
          <div className="bg-white rounded-3xl p-10 mt-8 shadow-sm border border-black/5 text-center animate-pop-in">
            <div
              className="w-16 h-16 rounded-3xl mx-auto flex items-center justify-center"
              style={{ backgroundColor: t.primary }}
            >
              <PartyPopper className="w-8 h-8 text-white" />
            </div>
            <h2 className="font-head font-extrabold text-2xl mt-5">Program complete!</h2>
            <p className="text-muted-foreground mt-2">
              You earned <b style={{ color: "#D4AF37" }}>+{sessionPoints} points</b> this session. Beautiful work.
            </p>
            <button
              onClick={restart}
              className="mt-6 inline-flex items-center gap-2 text-white font-semibold px-8 py-3.5 rounded-full hover:-translate-y-1 transition-transform shadow-sm hover:shadow-md"
              style={{ backgroundColor: t.primary }}
              data-testid="practice-again"
            >
              <RotateCcw className="w-4 h-4" /> Continue with new questions
            </button>
            <p className="text-xs text-muted-foreground mt-3">
              Fresh questions each round so you keep growing your knowledge.
            </p>
            <div className="flex gap-3 justify-center mt-7">
              <button
                onClick={() => navigate("/hub")}
                className="font-semibold px-7 py-3.5 rounded-full border border-black/10 hover:bg-black/5 transition-colors"
                data-testid="finish-to-hub"
              >
                Back to programs
              </button>
              <button
                onClick={() => navigate("/dashboard")}
                className="font-semibold px-7 py-3.5 rounded-full border border-black/10 hover:bg-black/5 transition-colors"
                data-testid="finish-to-dashboard"
              >
                View progress
              </button>
            </div>
          </div>
        )}

        {questions.length === 0 && !loading && (
          <div className="bg-white rounded-3xl p-10 mt-8 text-center shadow-sm border border-black/5">
            <p className="text-muted-foreground">No questions here yet. Please check back soon.</p>
          </div>
        )}
      </div>

      {/* Feedback drawer */}
      {result && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/30 px-4 pb-4 sm:pb-0 animate-fade-up">
          <div className="bg-white rounded-3xl p-7 w-full max-w-md shadow-xl animate-pop-in" data-testid="feedback-modal">
            <div className="flex items-center gap-3">
              <div
                className="w-12 h-12 rounded-2xl flex items-center justify-center"
                style={{ backgroundColor: result.is_correct ? "#48BB78" : "#F2EFEA" }}
              >
                {result.is_correct ? (
                  <Check className="w-6 h-6 text-white" />
                ) : (
                  <Sparkles className="w-6 h-6 text-[#718096]" />
                )}
              </div>
              <div>
                <h3 className="font-head font-extrabold text-lg" data-testid="feedback-status">
                  {result.is_correct ? "Nicely done!" : "Good effort"}
                </h3>
                {result.points_awarded > 0 && (
                  <p className="text-sm font-semibold" style={{ color: "#D4AF37" }}>
                    +{result.points_awarded} points
                  </p>
                )}
              </div>
            </div>

            {feedbackLoading && !result.ai_feedback ? (
              <p
                className="font-serif-accent text-[15px] leading-relaxed mt-5 text-[#A0AEC0] animate-pulse"
                data-testid="ai-feedback-loading"
              >
                Writing your feedback…
              </p>
            ) : (
              result.ai_feedback && (
                <p
                  className="font-serif-accent text-[15px] leading-relaxed mt-5 text-[#4A5568] animate-fade-up"
                  data-testid="ai-feedback"
                >
                  {result.ai_feedback}
                </p>
              )
            )}

            {result.explanation && (
              <div className="mt-4 rounded-2xl p-4 text-sm text-[#4A5568]" style={{ backgroundColor: t.bg }}>
                <b style={{ color: t.primary }}>Insight:</b> {result.explanation}
              </div>
            )}

            {result.new_badges?.length > 0 && (
              <div className="mt-4 space-y-2">
                {result.new_badges.map((b) => (
                  <div
                    key={b.id}
                    className="flex items-center gap-3 rounded-2xl p-3 bg-[#FFFBEB] border border-[#F6E05E]/40 animate-pop-in"
                    data-testid={`badge-unlock-${b.id}`}
                  >
                    <Award className="w-6 h-6" style={{ color: "#D4AF37" }} />
                    <div>
                      <p className="font-bold text-sm">Badge unlocked: {b.name}</p>
                      <p className="text-xs text-muted-foreground">{b.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-5 flex items-center justify-between">
              <span className="flex items-center gap-3 text-sm font-semibold">
                <span className="flex items-center gap-1">
                  <Trophy className="w-4 h-4" style={{ color: "#D4AF37" }} /> {result.total_points}
                </span>
                <span className="flex items-center gap-1">
                  <Flame className="w-4 h-4" style={{ color: "#FF7B54" }} /> {result.streak}
                </span>
              </span>
              <button
                onClick={next}
                className="flex items-center gap-1 text-white font-semibold px-6 py-3 rounded-full hover:-translate-y-0.5 transition-transform"
                style={{ backgroundColor: t.primary }}
                data-testid="next-question"
              >
                {idx + 1 < questions.length ? "Continue" : "Finish"} <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
